"use client";

import { useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { toast } from "react-toastify";
import { useTranslations } from "next-intl";
import { inviteMember } from "@/helpers/api/teams";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";

export function InviteMemberModal({
    isOpen,
    onClose,
    teamId,
    onSuccess,
}) {
    const t = useTranslations("teamsInvite");
    const [email, setEmail] = useState("");
    const [role, setRole] = useState("member");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!email.trim()) {
            setError(t("errorEmailRequired"));
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError(t("errorEmailInvalid"));
            return;
        }

        setLoading(true);

        try {
            const result = await inviteMember(teamId, email, role);
            toast.success(t("successInviteSent", { email }));
            setEmail("");
            setRole("member");
            onSuccess?.(result);
            onClose();
        } catch (err) {
            console.error("Error:", err);
            setError(err.message || t("errorInviteFailed"));
            toast.error(err.message || t("errorInviteFailed"));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setEmail("");
            setRole("member");
            setError(null);
            onClose();
        }
    };

    return (
        <BottomSheetModal
            isOpen={isOpen}
            onClose={handleClose}
            maxWidthClass="sm:max-w-md"
            zIndexClass="z-[100]"
        >
            {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-100">
                        <h2 className="text-2xl font-semibold text-gray-900 tracking-tight">
                            {t("title")}
                        </h2>
                        <button
                            onClick={handleClose}
                            disabled={loading}
                            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                        >
                            <SolarIcon name="close-circle-linear" className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        {/* Email */}
                        <div>
                            <label className="block text-sm font-medium text-gray-900 mb-2">
                                {t("email")}
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setError(null);
                                }}
                                placeholder={t("emailPlaceholder")}
                                className={`w-full px-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm ${
                                    error
                                        ? "border-red-500"
                                        : "border-gray-200"
                                }`}
                                disabled={loading}
                                autoFocus
                            />
                            {error && (
                                <p className="text-sm text-red-600 mt-2 mb-0">{error}</p>
                            )}
                        </div>

                        {/* Role */}
                        <div>
                            <label className="block text-sm font-medium text-gray-900 mb-2">
                                {t("role")}
                            </label>
                            <select
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                                className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm font-medium"
                                disabled={loading}
                            >
                                <option value="member">{t("roleMember")}</option>
                                <option value="manager">{t("roleManager")}</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-2 mb-0">
                                {role === "manager"
                                    ? t("roleManagerDescription")
                                    : t("roleMemberDescription")}
                            </p>
                        </div>

                        {/* Info Box */}
                        <div className="bg-gray-50 rounded-xl p-4">
                            <p className="text-sm text-gray-600 mb-0">
                                {t("infoBox")}
                            </p>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-3">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={loading}
                                className="flex-1 px-4 py-2.5 text-blue-600 rounded-xl font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                {t("cancel")}
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <SolarIcon name="refresh-linear" className="w-4 h-4 animate-spin" />
                                        <span>{t("sending")}</span>
                                    </>
                                ) : (
                                    <span>{t("send")}</span>
                                )}
                            </button>
                        </div>
                    </form>
        </BottomSheetModal>
    );
}
